import { Box, styled } from '@mui/material';
import { useRecoilState, useRecoilValue } from 'recoil';
import BrushDiameterLabel from '~/components/Viewer/BrushDiameterLabel/BrushDiameterLabel';
import PlaneViewFrame from '~/components/Viewer/Views/PlaneViewFrame';
import CoordinateDisplay from '~/containers/viewer/viewport/ViewportOverlay/CoordinateDisplay';
import CrosshairsComponent from '~/containers/viewer/viewport/ViewportOverlay/Crosshairs/index';
import DoseColorwashSlider from '~/containers/viewer/viewport/ViewportOverlay/DoseColorwashSlider';
import LabelDirections from '~/containers/viewer/viewport/ViewportOverlay/LabelDirections';
import SwapToFullScreen from '~/containers/viewer/viewport/ViewportOverlay/SwapToFullScreen';
import SwapToMain from '~/containers/viewer/viewport/ViewportOverlay/SwapToMain';
import WindowingLevelLabel from '~/containers/viewer/viewport/ViewportOverlay/WindowingLevelLabel';
import { Orientations, PlaneView } from '~/engine/view/views/PlaneView';
import { doseVisibilityState } from '~/state/atoms/dose/doseAtoms';
import { brushDiameterLabelVisibleState } from '~/state/atoms/toolsAtoms';
import {
    showFullscreenState,
    viewportOrientationOrderState,
} from '~/state/atoms/viewerAtoms';
import { ImageSlicePositions } from '../Viewport';

const ViewportOverlayWrap = styled(Box)`
    width: 100%;
    height: 100%;
    position: relative;
    border: 1px solid ${({ theme }) => theme.palette.primary.dark};
    border-radius: ${({ theme }) => theme.shape.borderRadius}px;
`;

const ViewportOverlaySectionWrap = styled(Box)`
    position: absolute;
    z-index: 15;
`;

interface ViewportOverlayProps {
    view: PlaneView | null;
    viewportOrientation: Orientations;
    canvas: React.RefObject<HTMLCanvasElement | null>;
    canvas2D: React.RefObject<HTMLCanvasElement | null>;
    isActive: boolean;
    getNewCurrentImageSlice: () => ImageSlicePositions;
}

const ViewportOverlay = ({
    view,
    viewportOrientation,
    canvas,
    canvas2D,
    isActive,
    getNewCurrentImageSlice,
}: ViewportOverlayProps) => {
    const viewportOrientationOrder = useRecoilValue(
        viewportOrientationOrderState
    );
    const isMainViewport = viewportOrientationOrder[0] === viewportOrientation;
    const [showFullscreen, setShowFullscreen] =
        useRecoilState(showFullscreenState);
    const doseVisibility = useRecoilValue(doseVisibilityState);
    const brushDiameterLabelVisible = useRecoilValue(
        brushDiameterLabelVisibleState
    );

    return (
        <ViewportOverlayWrap>
            {isMainViewport && doseVisibility && (
                <ViewportOverlaySectionWrap left={12} top={8}>
                    <DoseColorwashSlider />
                </ViewportOverlaySectionWrap>
            )}
            <ViewportOverlaySectionWrap top={8} right={12}>
                {isMainViewport ? (
                    <SwapToFullScreen
                        showFullscreen={showFullscreen}
                        setShowFullscreen={setShowFullscreen}
                    />
                ) : (
                    <SwapToMain viewportOrientation={viewportOrientation} />
                )}
            </ViewportOverlaySectionWrap>
            {isMainViewport && (
                <ViewportOverlaySectionWrap bottom={8} left={8}>
                    {brushDiameterLabelVisible && <BrushDiameterLabel />}
                    <WindowingLevelLabel />
                </ViewportOverlaySectionWrap>
            )}
            <LabelDirections viewportOrientation={viewportOrientation} />
            <ViewportOverlaySectionWrap bottom={8} right={8}>
                <CoordinateDisplay
                    viewportOrientation={viewportOrientation}
                    isActive={isActive}
                />
            </ViewportOverlaySectionWrap>
            <CrosshairsComponent view={view} />
            <PlaneViewFrame
                view={view}
                canvas={canvas}
                viewportOrientation={viewportOrientation}
                canvas2D={canvas2D}
                getNewCurrentImageSlice={getNewCurrentImageSlice}
            />
        </ViewportOverlayWrap>
    );
};

export default ViewportOverlay;
